
import { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { toast } from "sonner";
import { Gerador } from "@/types";
import { GeradorService } from "@/services/StorageService";

// Import refactored components
import FormHeader from "./edit/FormHeader";
import BasicInfoFields from "./edit/BasicInfoFields";
import AddressFields from "./edit/AddressFields";
import FinancialFields from "./edit/FinancialFields";
import CalculatedValues from "./edit/CalculatedValues";
import { useGeradorCalculations } from "./edit/useGeradorCalculations";

const geradorEditSchema = z.object({
  nomeTitular: z.string().min(3, "Nome deve ter no mínimo 3 caracteres"),
  documento: z.string().min(11, "CPF/CNPJ inválido"),
  cep: z.string().min(8, "CEP inválido"),
  logradouro: z.string().min(1, "Logradouro é obrigatório"),
  bairro: z.string().min(1, "Bairro é obrigatório"),
  cidade: z.string().min(1, "Cidade é obrigatória"),
  estado: z.string().length(2, "Estado deve ter 2 caracteres"),
  concessionaria: z.string().min(1, "Selecione uma concessionária"),
  potenciaInstalada: z.number().positive("Potência deve ser maior que zero"),
  geracaoEstimada: z.number().positive("Geração estimada deve ser maior que zero"),
  tarifaVenda: z.number().positive("Tarifa de venda deve ser maior que zero"),
  custoInvestimento: z.number().positive("Custo de investimento deve ser maior que zero")
});

type GeradorEditValues = z.infer<typeof geradorEditSchema>;

interface GeradorEditFormProps {
  gerador: Gerador;
  onSave: (updatedGerador: Gerador) => void;
  onCancel: () => void;
}

const GeradorEditForm = ({ gerador, onSave, onCancel }: GeradorEditFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<GeradorEditValues>({
    resolver: zodResolver(geradorEditSchema),
    defaultValues: {
      nomeTitular: gerador.nomeTitular,
      documento: gerador.documento,
      cep: gerador.cep,
      logradouro: gerador.logradouro,
      bairro: gerador.bairro,
      cidade: gerador.cidade,
      estado: gerador.estado,
      concessionaria: gerador.concessionaria,
      potenciaInstalada: gerador.potenciaInstalada,
      geracaoEstimada: gerador.geracaoEstimada,
      tarifaVenda: gerador.tarifaVenda,
      custoInvestimento: gerador.custoInvestimento
    }
  });
  
  // Use custom hook for calculations
  const { valorGeracaoEstimada, payback, valorKwhMes } = useGeradorCalculations(
    form.watch("geracaoEstimada"),
    form.watch("tarifaVenda"),
    form.watch("custoInvestimento")
  );

  const onSubmit = async (values: GeradorEditValues) => {
    setIsSubmitting(true);
    try {
      const updatedGerador = GeradorService.update(gerador.id, {
        ...gerador,
        ...values
      });
      
      if (updatedGerador) {
        toast.success("Gerador atualizado com sucesso!");
        onSave(updatedGerador);
      } else {
        toast.error("Erro ao atualizar gerador");
      }
    } catch (error) {
      console.error("Erro ao atualizar gerador:", error);
      toast.error("Erro ao atualizar gerador");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCepFound = (address: any) => {
    form.setValue("cep", address.cep);
    form.setValue("logradouro", address.logradouro);
    form.setValue("bairro", address.bairro);
    form.setValue("cidade", address.localidade);
    form.setValue("estado", address.uf);
  };

  return (
    <div className="space-y-6">
      <FormHeader 
        isSubmitting={isSubmitting} 
        onCancel={onCancel} 
        onSave={form.handleSubmit(onSubmit)} 
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <BasicInfoFields form={form} onAddressFound={handleCepFound} />
          <AddressFields form={form} onAddressFound={handleCepFound} />
          <FinancialFields form={form} />
          <CalculatedValues 
            valorGeracaoEstimada={valorGeracaoEstimada} 
            payback={payback} 
            valorKwhMes={valorKwhMes} 
          />
        </form>
      </Form>
    </div>
  );
};

export default GeradorEditForm;
